import log from './log.js';

class ResponseHandler {
  constructor(request, on_complete, timeout) {
    this.request = request;
    this.onComplete = on_complete;
    this.timeout = timeout;
  }

  invokeCallback(msg) {
    clearTimeout(this.timeout);
    this.onComplete(msg);
  }
}

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}

class FailureHandler {
  constructor(store, translationservice, request, response) {
    this.store = store;
    this.translationservice = translationservice;
    var id = 'js-' + uuidv4();
    if (response.exceptionData && response.exceptionData.id) {
      id = response.exceptionData.id;
    }
    var message = "Unspecified error";
    if (response.exceptionName && response.exceptionName != "InternalException") {
      message = response.exceptionName;
    }
    if (request && request.data && request.data.credentials && request.data.credentials.password) {
      request.data.credentials.password = "";
    }
    this.failure = {
      request: JSON.parse(JSON.stringify(request)), // make a copy to avoid "do not mutate vuex store state" errors
      response: response,
      id: id,
      level: 0,
      message: message
    };
    this.store.dispatch({ type: 'errors/addError', id: this.failure.id, error: this.failure });
  }

  set(msg, level) {
    var message = msg;
    if (this.translationservice && this.translationservice.exists) {
      message = this.translationservice.t(msg);
    }
    var level = 1;
    if (level && level > 1) {
      level = level;
    }
    this.store.dispatch({ type: 'errors/updateError', id: this.failure.id, message, level });
  }

  remove() {
    this.store.dispatch({ type: 'errors/removeError', id: this.failure.id });
  }

}

// NOTE: multiple subscribes to same topic with different filters currently not supported

class SubscriptionTopic {
  constructor(websocket, subscribe_msg, unsubscribe_msg) {
    this.ws = websocket;
    this.subscribeMsg = subscribe_msg;
    this.unsubscribeMsg = unsubscribe_msg;
    this.subscriptions = [];
    this.connectCallbackFunc = null;
    this.subscribePromise = null;
    this.subscribeResolve = null;
    this.subscribeReject = null;
  }

  subscribe(success_callback, handlers, timeout) {
    if (handlers) {
      for (var messagename in handlers) {
        this.ws.addHandler(messagename, handlers[messagename]);
      }
    }

    if (!this.subscribePromise) {
      this.subscribePromise = new Promise((resolve, reject) => {
        this.subscribeResolve = resolve;
        this.subscribeReject = reject;
      });
    }

    const sub = {
      success_callback: success_callback,
      handlers: handlers,
      promise: this.subscribePromise

      // define unsubscribe function
    };sub.unsubscribe = callback => {
      // remove sub from subscriptions list
      this.subscriptions = this.subscriptions.filter(obj => obj !== sub);
      // remove message handlers
      if (handlers) {
        for (var messagename in handlers) {
          this.ws.removeHandler(messagename, handlers[messagename]);
        }
      }
      // send unsubscribe request to server if we have no more subscriptions to this topic
      if (this.subscriptions.length === 0) {
        log.debug('Unsubscribed last listeners, unsubscribing to server with message ' + JSON.stringify(this.unsubscribeMsg));
        this.sendUnsubscribe(callback);

        // remove on connected callback
        this.ws.removeOnConnectedCallback(this.connectCallbackFunc);
        this.connectCallbackFunc = null;
      } else {
        log.debug('Unsubscribed listeners from topic ' + JSON.stringify(this.subscribeMsg) + " " + this.subscriptions.length + " listeners still exist, keeping subscription to server alive.");
      }
    };

    sub.addHandler = (messagename, callback) => {
      if (!handlers) handlers = {};
      handlers[messagename] = { topicId: sub.topicId, callback: callback };
      this.ws.addHandler(messagename, handlers[messagename]);
    };

    this.subscriptions.push(sub);

    if (this.ws.isConnected()) {
      this.sendSubscribe(timeout);
    }

    if (!this.connectCallbackFunc) {
      this.connectCallbackFunc = this.ws.addOnConnectedCallback(() => {
        this.sendSubscribe(timeout);
      });
    }

    return sub;
  }

  sendSubscribe(timeout) {
    this.ws.send(this.subscribeMsg, response => {
      for (let sub of this.subscriptions) {
        if (sub.success_callback) {
          sub.success_callback(response);
          sub.success_callback = null;
        }
      }
      if (response.status === 'success' && response.data != null && !response.data.exceptionName != null) {
        this.subscribeResolve(response.data);
      } else {
        if (this.ws.store) {
          this.subscribeReject(new FailureHandler(this.ws.store, this.ws.translationService, this.subscribeMsg, response));
        } else {
          this.subscribeReject(response);
        }
      }
    }, timeout);
  }

  sendUnsubscribe(callback) {
    this.ws.send(this.unsubscribeMsg, response => {
      if (typeof callback === 'function') {
        callback(response);
      }
      //    log.debug('Unsubscribe to server ' + JSON.stringify(this.unsubscribeMsg) + " completed with response: " + JSON.stringify(response))
    }, null, false);
  }

  unsubscribeAll() {
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
}

export default class ReactorWebSocket {
  constructor() {
    this.socket = null;
    this.connectUrl = null;
    this.unloading = false;
    this.reconnecting = false;
    this.isPublic = false;
    this.onConnectedCallbacks = [];
    this.onDisconnectedCallbacks = [];
    this.onReconnectedCallbacks = [];
    this.handlers = {};
    this.subscriptionTopics = new Map();
    this.responseHandlers = new Map();
    this.ALLMSG = "all_messages";
    this.requestId = 0;
    this.websocketId = uuidv4();

    this.reconnectAttempts = 0;

    this.reconnectTimeoutHandle = null;

    this.authorizationRequests = [];
    this.requestQueue = [];
    this.store = null;
    this.translationService = null;

    this.sendKeepAliveTimerHandle = null;
    this.SENDKEEPALIVE_INTERVAL = 1000 * 20; //in ms
    this.DEFAULT_REQUEST_TIMEOUT = 30000;
    this.openHasBeenCalled = false;
    this.onErrorBeforeOpen = null;

    this.lostFocusTime = null;
    this.gotFocusTime = null;

    window.onbeforeunload = () => {
      this.log('Page is unloading');
      this.unloading = true;
      this.disconnect();
    };

    window.onblur = () => {
      if (this.isMobileSafari()) {
        this.lostFocusTime = Date.now();
      }
    };

    window.onfocus = () => {
      if (this.isMobileSafari()) {
        this.gotFocusTime = Date.now();
        //check this only if the app has not been active for more than half a minute
        if (this.lostFocusTime != null && this.gotFocusTime - this.lostFocusTime > 1000 * 30) {
          //if we have had the browser in the background or awaked the device, the web socket connection will fail when the app is opened again
          //it will then reconnect automatically
          this.lostFocusTime = null;
          setTimeout(() => {
            if (this.isConnected()) {
              //However in some intermittent cases, the websocket connection doesn't fail! It is possible though that there is nothing in the other end listening to it.
              //Reconnect to ensure we're in state
              this.log('Force reconnect');
              this.disconnect();
              this.reconnecting = false;
              this.reconnect(true);
            }
          }, 100);
        } else {
          this.lostFocusTime = null;
        }
      }
    };
  }

  isMobileSafari() {
    if (window.navigator.userAgent) {
      if (window.navigator.userAgent.includes("Safari")) {
        if (window.navigator.userAgent.includes("Mobile")) {
          return true;
        }
      }
    }
    return false;
  }

  log(message) {
    log.info(message);
  }

  setDefaultTimeout(timeout_ms) {
    this.DEFAULT_REQUEST_TIMEOUT = timeout_ms;
  }

  sendKeepAlive() {
    this.log("Sending websocket keepalive");
    if (this.isConnected()) {
      this.socket.send("keepalive");
      this.resetKeepAlive();
    }
  }

  resetKeepAlive() {
    clearTimeout(this.sendKeepAliveTimerHandle);
    this.sendKeepAliveTimerHandle = setTimeout(() => {
      this.sendKeepAlive();
    }, this.SENDKEEPALIVE_INTERVAL);
  }

  addHandler(name, func) {
    if (!this.handlers[name]) {
      this.handlers[name] = [func];
    } else {
      this.handlers[name].push(func);
    }
  }

  addOnMessageHandler(func) {
    this.addHandler(this.ALLMSG, func);
  }

  removeHandler(name, func) {
    if (this.handlers[name]) {
      var funcPos = this.handlers[name].lastIndexOf(func);
      if (funcPos > -1) {
        this.handlers[name].splice(funcPos, 1);
      }
    }
  }

  removeOnMessageHandler(func) {
    this.removeHandler(this.ALLMSG, func);
  }

  addOnConnectedCallback(func) {
    this.onConnectedCallbacks.push(func);
    return func;
  }

  removeOnConnectedCallback(func) {
    var funcPos = this.onConnectedCallbacks.indexOf(func);
    if (funcPos > -1) {
      this.onConnectedCallbacks.splice(funcPos, 1);
    }
  }

  addOnDisconnectedCallback(func) {
    this.onDisconnectedCallbacks.push(func);
    return func;
  }

  removeOnDisconnectedCallback(func) {
    var funcPos = this.onDisconnectedCallbacks.indexOf(func);
    if (funcPos > -1) {
      this.onDisconnectedCallbacks.splice(funcPos, 1);
    }
  }

  addOnReconnectedCallback(func) {
    this.onReconnectedCallbacks.push(func);
    return func;
  }

  removeOnReconnectedCallback(func) {
    var funcPos = this.onReconnectedCallbacks.indexOf(func);
    if (funcPos > -1) {
      this.onReconnectedCallbacks.splice(funcPos, 1);
    }
  }

  setVuexStore(store) {
    this.store = store;
  }

  setTranslationService(service) {
    this.translationService = service;
  }

  subscribeWithTopicAndFilter(topic, filter, success_callback, handlers, timeout) {
    var parts = topic.split("/");
    var subscribe_msg = {
      service: parts[1],
      message: "Subscribe",
      data: {
        topic: topic,
        filter: filter
      }
    };
    var unsubscribe_msg = {
      service: parts[1],
      message: "Unsubscribe",
      data: {
        topic: topic
      }
    };
    return this.subscribeWithHandlers(subscribe_msg, unsubscribe_msg, success_callback, handlers, timeout);
  }

  subscribeWithHandlers(subscribe_msg, unsubscribe_msg, success_callback, handlers, timeout) {
    var topic_key = JSON.stringify(subscribe_msg);
    this.log('Subscribe - ' + topic_key);
    var topicHandler = this.subscriptionTopics.get(topic_key);
    if (!topicHandler) {
      topicHandler = new SubscriptionTopic(this, subscribe_msg, unsubscribe_msg);
      this.subscriptionTopics.set(topic_key, topicHandler);
    }
    return topicHandler.subscribe(success_callback, handlers, timeout);
  }

  subscribe(topic, filter, success_callback, timeout) {
    return this.subscribeWithTopicAndFilter(topic, filter, success_callback, null, timeout);
  }

  isConnected() {
    return this.socket != null && this.socket.readyState === this.socket.OPEN;
  }

  isClosed() {
    return this.socket == null || this.socket.readyState === this.socket.CLOSED || this.socket.readyState === this.socket.CLOSING;
  }

  connect(url, onConnectedCallback) {
    if (!this.isClosed()) {
      return;
    }

    this.socket = new WebSocket(url + "?connection_id=" + this.websocketId);
    this.connectUrl = url;
    this.requestId = 1;
    this.openHasBeenCalled = false;

    this.socket.onopen = () => {
      this.onOpen();
    };
    this.socket.onmessage = message => {
      this.onMessage(message);
    };
    this.socket.onclose = () => {
      this.onClose();
    };
    this.socket.onerror = event => {
      this.onError(event);
    };
    if (onConnectedCallback) {
      this.addOnConnectedCallback(onConnectedCallback);
    }
  }

  connectToRegion(region_url) {
    if (region_url == null) return;

    let ws_url = region_url.replace("http://", "ws://").replace("https://", "wss://") + "/api";
    if (this.connectUrl != ws_url) {
      this.disconnect();
      this.connect(ws_url);
    }
  }

  URL() {
    return this.connectUrl;
  }

  regionURL() {
    return this.connectUrl.replace("ws://", "http://").replace("wss://", "https://").replace("/api", "");
  }

  onOpen() {
    this.openHasBeenCalled = true;
    this.log('WebSocket open');
    let do_reconnect_callback = this.reconnecting;
    this.reconnecting = false;

    this.reconnectAttempts = 0;

    var open_sequence = [];

    var loop_scope = (next_index, req) => {
      open_sequence.push(() => {
        this.send(req, json => {
          if (req.onComplete != null) {
            req.onComplete(json);
            req.onComplete = null;
          }
          open_sequence[next_index]();
        }, 60000);
      });
    };

    for (var i = 0; i < this.authorizationRequests.length; i++) {
      loop_scope(i + 1, this.authorizationRequests[i]);
    }

    open_sequence.push(() => {
      this.requestQueue.forEach(item => {
        clearTimeout(item.cancelTimeout);
        this.send(item.json, item.onComplete, item.timeout, false);
      });
      this.requestQueue = [];

      for (var callback of this.onConnectedCallbacks) {
        callback();
      }

      if (do_reconnect_callback) {
        for (var callback of this.onReconnectedCallbacks) {
          callback();
        }
      }
    });

    open_sequence[0]();

    this.resetKeepAlive();
  }

  getReconnectTimeout(n) {
    var max_timeout = Math.min((Math.pow(2, n) - 1) * 1000, 30 * 1000);
    return Math.random() * max_timeout;
  }

  reconnect(enforce) {
    if (this.reconnecting) {
      return;
    }

    this.reconnecting = true;

    if (!this.unloading) {
      if (this.reconnectTimeoutHandle != null) {
        clearTimeout(this.reconnectTimeoutHandle);
      }
      this.reconnectTimeoutHandle = setTimeout(() => {
        this.log('Current socket ready state: ' + this.socket.readyState);
        if (enforce || this.socket.readyState === this.socket.CLOSED) {
          this.log('Reconnecting... ');
          this.connect(this.connectUrl);
          this.reconnecting = false;
        }
        this.reconnect();
      }, this.getReconnectTimeout(this.reconnectAttempts++));
    }
  }

  clear() {
    this.subscriptionTopics.forEach(topic => {
      topic.unsubscribeAll();
    });
    this.subscriptionTopics = new Map();
    this.authorizationRequests = [];
    this.requestQueue = []; //don't want to send requests if session has expired
  }

  disconnect() {
    this.log('Disconnecting');
    if (this.socket) {
      this.socket.onclose = () => {}; // disable onclose handler
      this.socket.close();
    }
    clearTimeout(this.sendKeepAliveTimerHandle);
    clearTimeout(this.reconnectTimeoutHandle);

    //discard any queued requests. requestId:s get reset when connecting again so would be weird to keep them
    let request_queue = this.requestQueue;
    this.requestQueue = [];
    request_queue.forEach(item => {
      clearTimeout(item.cancelTimeout);
      if (item.onComplete != null) {
        item.onComplete({ status: "error", exceptionName: "Disconnect" });
      }
    });

    //fail any requests that have been sent and are awaiting response
    //need to clear this.responseHandlers before calling any callbacks because they may call this function back
    let response_handlers = this.responseHandlers;
    this.responseHandlers = new Map();
    for (let response_handler of response_handlers.values()) {
      response_handler.invokeCallback({ status: "error", exceptionName: "Disconnect" });
    }
  }

  onClose() {
    this.log('Connection closed');
    for (var i = 0; i < this.onDisconnectedCallbacks.length; i++) {
      this.onDisconnectedCallbacks[i]();
    }
    this.reconnecting = false;
    clearTimeout(this.sendKeepAliveTimerHandle);
    this.reconnect();
  }

  onError(event) {
    this.log("Connection error");
    if (!this.openHasBeenCalled) {
      if (this.onErrorBeforeOpen) this.onErrorBeforeOpen();
    }
  }

  onMessage(message) {
    var msgJson = JSON.parse(message.data);

    //reset keepalive timer because we just got something down the pipe
    this.resetKeepAlive();

    if (!msgJson) {
      return;
    }

    var msgType = msgJson.message;
    var msgData = msgJson.data;
    var msgTopicId = msgJson.topicId;
    var msgRequestId = msgJson.requestId;

    if (msgJson.exceptionName != undefined) {
      msgType = msgJson.exceptionName;
      msgData = msgJson.exceptionData;
    }

    if (msgRequestId != undefined) {
      var responseHandler = this.responseHandlers.get(msgRequestId);
      if (responseHandler) {
        this.log("Handling response request: " + responseHandler.request.message + " #" + msgRequestId);
        this.responseHandlers.delete(msgRequestId);
        responseHandler.invokeCallback(msgJson);
      } else {
        this.log("No response handler found #" + msgRequestId + ", message:" + message.data);
      }
    }
    // "all" message handler
    if (this.handlers[this.ALLMSG] && Array.isArray(this.handlers[this.ALLMSG])) {
      this.log("Handling broadcast: " + msgJson.message);
      for (var i = 0; i < this.handlers[this.ALLMSG].length; i++) {
        if (typeof this.handlers[this.ALLMSG][i] === 'function') {
          this.handlers[this.ALLMSG][i](msgJson);
        } else if ((this.handlers[this.ALLMSG][i].topicId && this.handlers[this.ALLMSG][i].topicId == msgTopicId || this.handlers[this.ALLMSG][i].topicId == null) && typeof this.handlers[this.ALLMSG][i].callback === 'function') {
          this.handlers[this.ALLMSG][i](msgJson);
        }
      }
    }
    // standard message handler
    if (this.handlers[msgType] && Array.isArray(this.handlers[msgType])) {
      this.log("Handling broadcast: " + msgJson.message);
      for (var i = 0; i < this.handlers[msgType].length; i++) {
        if (typeof this.handlers[msgType][i] === 'function') {
          this.handlers[msgType][i](msgData);
        } else if ((this.handlers[msgType][i].topicId && this.handlers[msgType][i].topicId == msgTopicId || this.handlers[msgType][i].topicId == null) && typeof this.handlers[msgType][i].callback === 'function') {
          this.handlers[msgType][i].callback(msgData);
        }
      }
    }
  }

  sendPromise(json, timeout) {
    return new Promise((resolve, reject) => {
      this.send(json, response => {
        if (response.status === 'success') resolve(response);else reject(response);
      }, timeout);
    });
  }

  send(json, onComplete, timeout, queue_if_not_connected) {
    if (!timeout) {
      timeout = this.DEFAULT_REQUEST_TIMEOUT;
    }
    if (!json.timeout) {
      json.timeout = timeout;
    }
    json.requestId = ++this.requestId;

    if (this.isConnected()) {
      this.log("Sending request: " + json.message + " #" + json.requestId);
      if (onComplete) {
        // use a string as key, this of course means that accessing the map must be done with a string as well (not int)
        var requestIdKey = json.requestId.toString();

        this.responseHandlers.set(requestIdKey, new ResponseHandler(json, onComplete, setTimeout(() => {
          var responseHandler = this.responseHandlers.get(requestIdKey);
          if (responseHandler) {
            this.log("Removing response handler " + requestIdKey + " due to timeout");
            this.responseHandlers.delete(requestIdKey);
            responseHandler.invokeCallback({ status: "error", exceptionName: "Timeout" });
          } else {
            console.log("No response handler available when timeout happened. Perhaps timeout was not cancelled? " + json.message + " #" + requestIdKey);
          }
        }, timeout)));
      }

      this.socket.send(JSON.stringify(json));

      //reset keepalive timer because we just sent something down the pipe
      this.resetKeepAlive();
    } else {
      if (queue_if_not_connected == null || queue_if_not_connected) {
        this.log("Queueing request: " + json.message);
        let cancel = setTimeout(() => {
          this.log("Removing timed out request " + json.requestId + " from queue");
          this.requestQueue = this.requestQueue.filter(item => item.json.requestId !== json.requestId);
          onComplete({ status: "error", exceptionName: "Timeout" });
        }, timeout);
        this.requestQueue.push({ timestamp: Date.now(), json: json, onComplete: onComplete, timeout: timeout, cancelTimeout: cancel });
      } else {
        this.log("Cannot send message as socket is not ready: " + json.message);
        if (onComplete) {
          onComplete({ status: "error", exceptionName: "WebSocketNotReady" });
        }
      }
    }
  }

  sendServiceRequest(serviceId, message, data, onComplete, timeout) {
    var json = { service: serviceId, message: message, data: data };
    return new Promise((resolve, reject) => {
      this.send(json, response => {
        if (onComplete) {
          onComplete(response);
        }
        if (response.status === 'success') {
          resolve(response.data);
        } else {
          if (this.store) {
            reject(new FailureHandler(this.store, this.translationService, json, response));
          } else {
            reject(response);
          }
        }
      }, timeout);
    });
    //    return this.send({ service: serviceId, message: message, data: data }, onComplete, timeout)
  }

  sendServiceRequestPromise(serviceId, message, data, timeout) {
    return this.sendPromise({ service: serviceId, message: message, data: data }, timeout);
  }

  sendAuthorizationRequest(json, onComplete, timeout) {
    if (this.isConnected()) {
      //send auth request immediately if we are connected
      this.send(json, onComplete, timeout);
    } else {
      json.onComplete = onComplete;
    }

    //check if we already have requested this authorization, if we have we shouldn't add another one to the list
    var new_msg_string = JSON.stringify(json);
    for (var i = 0; i < this.authorizationRequests.length; i++) {
      if (JSON.stringify(this.authorizationRequests[i]) === new_msg_string) {
        return;
      }
    }

    //add to authorization requests, sent when socket is opened
    this.authorizationRequests.push(json);
  }

  removeAuthorizationRequest(message_name) {
    this.authorizationRequests = this.authorizationRequests.filter(item => item.message !== message_name);
  }

  resumeSession(session_id, tenancy_id, onComplete, timeout) {
    // remove any old resume session requests from authorizationRequests list
    this.authorizationRequests = this.authorizationRequests.filter(item => item.message !== "ResumeSessionRequest");

    var msg = {
      service: "httpapiservice-1_0",
      message: "ResumeSessionRequest",
      data: {
        sessionId: session_id,
        tenancyId: tenancy_id
      }
    };
    this.sendAuthorizationRequest(msg, onComplete, timeout);
  }

  setConnectionInfo(data, onComplete, timeout) {
    var msg = {
      service: "httpapiservice-1_0",
      message: "SetConnectionInfo",
      data: data
    };
    this.removeAuthorizationRequest("SetConnectionInfo");
    this.sendAuthorizationRequest(msg, onComplete, timeout);
  }

  unsubscribeAllTopics() {
    this.subscriptionTopics.forEach(topic => {
      topic.unsubscribeAll();
    });
  }

}


//////////////////
// WEBPACK FOOTER
// ../ReactorJSCore/dist/websocket.js
// module id = null
// module chunks = 